import React, { useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import { } from '@lockerpm/design';

import { PlusOutlined } from "@ant-design/icons";

import commonComponents from "../../../components/common";
import itemsComponents from "../../../components/items";

import Filter from "./components/members/Filter";
import TableData from "./components/members/TableData";
import BoxData from "./components/members/BoxData";
import FormData from "./components/members/FormData";
import Review from "./components/members/Review";

import global from "../../../config/global";
import workspaceServices from "../../../services/workspace";
import mailConfigServices from "../../../services/mail-config";
import { getRouterByLocation, scrollEnd, isEmpty } from '../../../utils/common';

const Members = () => {
  const { PageHeader } = commonComponents;
  const { NoData, Pagination } = itemsComponents;
  const { t } = useTranslation();

  const isMobile = useSelector((state) => state.system.isMobile)
  const locale = useSelector((state) => state.system.locale)

  const currentPage = getRouterByLocation(window.location);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [formVisible, setFormVisible] = useState(false);
  const [reviewVisible, setReviewVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newMembers, setNewMembers] = useState([]);
  const [mailConfig, setMailConfig] = useState(null);
  const [data, setData] = useState([]);
  const [params, setParams] = useState({
    role: '',
    status: '',
    page: 1,
    size: global.constants.PAGE_SIZE,
  });

  useEffect(() => {
    if (currentPage.params?.workspace_id) {
      fetchData();
      fetchMailConfiguration();
    }
  }, [JSON.stringify(params)])
  
  useEffect(() => {
    setParams({
      ...params,
      page: 1,
      size: global.constants.PAGE_SIZE
    })
  }, [isMobile])

  const handleChangePage = (page, size) => {
    setParams({
      ...params,
      page,
      size
    })
  };

  const handleOpenForm = (item = null) => {
    setSelectedItem(item);
    setFormVisible(true)
  }

  const handleOpenReview = (members = []) => {
    setNewMembers(members);
    setReviewVisible(true)
  }

  const fetchData = () => {
    setLoading(true)
    workspaceServices.members(currentPage.params?.workspace_id, params).then((response) => {
      setData(response.results);
      setTotal(response.count)
      setLoading(false)
    }).catch(() => {
      setData([])
      setTotal(0)
      setLoading(false)
    })
  }

  const fetchMailConfiguration = async () => {
    await mailConfigServices.get(currentPage.params?.workspace_id).then((response) => {
      const config = response && !isEmpty(response) ? response : null;
      setMailConfig(config)
    }).catch(() => {
      setMailConfig(null)
    })
  }

  const handleDelete = (member) => {
    global.confirm(() => {
      workspaceServices.remove_member(currentPage.params?.workspace_id, member.id).then(async () => {
        global.pushSuccess(t('notification.success.member.deleted'));
        if (data.length === 1 && params.page > 1) {
          setParams({
            ...params,
            page: params.page - 1
          })
        }
        fetchData();
      }).catch((error) => {
        global.pushError(error)
      });
    });
  };

  const handleReinvite = (member) => {
    workspaceServices.reinvite(currentPage.params?.workspace_id, member.id).then(async () => {
      global.pushSuccess(t('notification.success.member.reinvited'));
    }).catch((error) => {
      global.pushError(error)
    });
  };

  return (
    <div
      className="members layout-content"
      onScroll={(e) => scrollEnd(e, params, total, setParams)}
    >
      <PageHeader
        title={t('members.title')}
        subtitle={t('members.description')}
        docLabel={t('members.doc')}
        docLink={global.urls.DOC_LINKS[locale].WS_SETTINGS_MEMBERS}
        actions={[
          {
            key: 'add',
            label: t('button.new_member'),
            type: 'primary',
            icon: <PlusOutlined />,
            click: () => handleOpenForm()
          }
        ]}
      />
      {
        <Filter
          className={'mt-6'}
          params={params}
          setParams={(v) => setParams({ ...v, page: 1 })}
        />
      }
      {
        total == 0 && <NoData
          loading={loading}
          className={'mt-6'}
        />
      }
      {
        total > 0 && <>
          {
            isMobile ? <BoxData
              className="mt-4"
              loading={loading}
              data={data}
              params={params}
              mailConfig={mailConfig}
              onDelete={handleDelete}
              onReinvite={handleReinvite}
            /> : <TableData
              className="mt-4"
              loading={loading}
              data={data}
              params={params}
              mailConfig={mailConfig}
              onDelete={handleDelete}
              onReinvite={handleReinvite}
            />
          }
        </>
      }
      {
        total > global.constants.PAGE_SIZE && !isMobile && <Pagination
          params={params}
          total={total}
          onChange={handleChangePage}
        />
      }
      <FormData
        visible={formVisible}
        item={selectedItem}
        onClose={() => setFormVisible(false)}
        onReload={() => fetchData()}
        onReview={handleOpenReview}
      />
      <Review
        visible={reviewVisible}
        members={newMembers}
        mailConfig={mailConfig}
        currentPage={currentPage}
        onClose={() => setReviewVisible(false)}
      />
    </div>
  );
}

export default Members;